import { useState } from 'react';
import {
  depositAmount,
  describeDepositTerms,
  formatCurrency,
  isOpenQuote,
  type Quote,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { useAcceptQuote } from '../../hooks/useNeeds';
import { extractApiErrorMessage } from '../../utils/apiError';

export interface AcceptQuoteModalProps {
  needId: string;
  /** The quote being accepted; null closes the dialog. */
  quote: Quote | null;
  /** Every quote of the need, to say who the accept declines. */
  quotes: Quote[];
  onClose: () => void;
  onAccepted?: () => void;
}

/**
 * Accepting books the need with this vendor and price and declines the other
 * open quotes. Telling those vendors is a choice made here, and off unless
 * ticked — the office decides who hears what.
 */
export function AcceptQuoteModal({
  needId,
  quote,
  quotes,
  onClose,
  onAccepted,
}: AcceptQuoteModalProps) {
  return (
    <EntityModal
      open={quote !== null}
      onClose={onClose}
      title="Accept quote"
      size="sm"
      presentation="centered"
    >
      {quote && (
        <AcceptForm
          key={quote.id}
          needId={needId}
          quote={quote}
          others={quotes.filter((q) => q.id !== quote.id && isOpenQuote(q.status))}
          onClose={onClose}
          onAccepted={onAccepted}
        />
      )}
    </EntityModal>
  );
}

function AcceptForm({
  needId,
  quote,
  others,
  onClose,
  onAccepted,
}: {
  needId: string;
  quote: Quote;
  others: Quote[];
  onClose: () => void;
  onAccepted?: () => void;
}) {
  const accept = useAcceptQuote(needId);
  const [notifyOthers, setNotifyOthers] = useState(false);
  const [error, setError] = useState('');
  const money = (n: number) => formatCurrency(n);
  const deposit = depositAmount(quote.depositTerms, quote.totalAmount);
  const reachable = others.filter((q) => q.vendorEmail);

  const submit = async () => {
    setError('');
    try {
      await accept.mutateAsync({ quoteId: quote.id, notifyOthers });
      onAccepted?.();
      onClose();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not accept the quote'));
    }
  };

  return (
    <div className="space-y-4">
      {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
      <p className="text-sm text-gray-700">
        Book <span className="font-medium">{quote.vendorName}</span> at{' '}
        <span className="font-medium">{money(quote.totalAmount ?? 0)}</span>.
      </p>
      {quote.depositTerms && (
        <p className="text-sm text-gray-600">
          Deposit: {describeDepositTerms(quote.depositTerms, money)}
          {deposit !== null && quote.depositTerms.percent !== undefined && ` (${money(deposit)})`}.
          Its day becomes the line’s due date.
        </p>
      )}
      {others.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            {others.length === 1
              ? `${others[0].vendorName}’s quote will be declined.`
              : `The other ${others.length} open quotes will be declined.`}
          </p>
          {reachable.length > 0 && (
            <label className="flex items-start gap-2 min-h-11 sm:min-h-0 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={notifyOthers}
                onChange={(e) => setNotifyOthers(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Tell the other vendors ({reachable.length})
                <span className="block text-xs text-gray-500">
                  A short note in each one’s conversation. Its wording is in Settings → Messaging.
                </span>
              </span>
            </label>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 sm:min-h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={accept.isPending}
          className="min-h-11 sm:min-h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {accept.isPending ? 'Accepting…' : 'Accept'}
        </button>
      </div>
    </div>
  );
}
